import { useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import { useCookies } from 'react-cookie';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useEffect, Fragment, useRef } from 'react';

import { FovLoader, FovOption, FovSelect, SearchInputField } from 'components';
import { getOauthClientsAdmin } from 'store/actions/adminActions/oauth';

import { MemberContainer } from './styles';
import { listColumns } from './ListColumns';
import { HeaderContainer, SortContainer } from 'pages/ArchVisionAdminDashboard/styles';

const AdminOauth = withTheme(props => {
    const { theme } = props;
    const dispatch = useDispatch();

    const userDataStore = useSelector(state => state.userData);
    const adminDataStore = useSelector(state => state.adminData);
    const searchFieldRef = useRef('');

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const selectedTheme = userDataStore.userPreferences.theme;
    const clients = useMemo(() => adminDataStore.clients, [ adminDataStore.clients ]);
    const isLoading = useMemo(() => adminDataStore.isOauthLoading, [ adminDataStore.isOauthLoading ]);
    const pagination = useMemo(() => adminDataStore.clientsPagination, [ adminDataStore.clientsPagination ]);
    const { page, pageSize, totalCount } = pagination;

    const [ searchParams, setSearchParams ] = useSearchParams();

    useEffect(() => {
        if (!clients || clients?.length === 0) {
            dispatch(getOauthClientsAdmin(token));
        }
    }, []);

    const handlePageChange = page => {
        if (Number(searchParams.get('page')) === page) return;
        setSearchParams(searchParams);
        dispatch(getOauthClientsAdmin(token));
    };

    const handleBlur = () => {
        const value = searchFieldRef.current.value;

        if (value === '') {
            searchParams.delete('search');
            searchParams.set('page', 1);
            setSearchParams(searchParams);
            dispatch(getOauthClientsAdmin(token));
            return;
        }

        searchParams.set('search', value);
        searchParams.set('page', 1);
        setSearchParams(searchParams);
        dispatch(getOauthClientsAdmin(token));
    };

    const sortOptions = [
        { key: 'NAME_ASC', text: 'Name (A-Z)', orderKey: 'order_by' },
        { key: 'NAME_DESC', text: 'Name (Z-A)', orderKey: 'order_by' },
    ];

    const buildSortOptions = () => sortOptions.map(option => (
        <FovOption key={option.key} value={option.key}>
            {option.text}
        </FovOption>
    ));

    const selectedSortOption = sortOptions.find(opt => {
        return searchParams.get('order_by') === opt.key;
    });

    return (
        <FovLoader
            text='Retrieving clients data'
            resource={!isLoading}
        >
            {clients?.length === 0 && <div>No clients.</div>}
            {clients?.length > 0 &&
                <Fragment>
                    <HeaderContainer>
                        <SearchInputField
                            width={400}
                            disabled={false}
                            ref={searchFieldRef}
                            defaultValue={searchFieldRef.current?.value || searchParams.get('search')}
                            onChange={e => searchFieldRef.current.value = e.target.value}
                            onKeyUp={e => e.key === 'Enter' && e.target.blur()}
                            onBlur={handleBlur}
                        />
                        <SortContainer>
                            <FovSelect
                            value={selectedSortOption?.key || ''}
                            placeholder={'Sort by'}
                            onChange={(e,newVal) => {
                                const sortOption = sortOptions.find(opt => opt.key === newVal);
                                searchParams.delete('order_by');
                                searchParams.set(sortOption.orderKey, sortOption.key);
                                searchParams.set('page', 1);
                                setSearchParams(searchParams);
                                dispatch(getOauthClientsAdmin(token));
                            }}
                            >
                                {buildSortOptions()}
                            </FovSelect>
                        </SortContainer>
                    </HeaderContainer>
                    <MemberContainer
                        dataSource={clients}
                        $selectedTheme={selectedTheme}
                        columns={listColumns(theme, selectedTheme)}
                        rowKey={'row-' + Math.random()}
                        currentPage={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        isPagination={true}
                        selectedKey={'client_id'}
                        onPageChange={handlePageChange}
                    />
                </Fragment>
            }
        </FovLoader>
    );
});

export default AdminOauth;
